import { APP_ROUTES } from '@zorro/common-utils';
import { Alert } from '@zorro/design-system/v2/Alert';
import { Card } from '@zorro/design-system/v2/Card';
import { AddUserIcon, InvoicesIcon, PayrollIcon } from '@zorro/shared/assets';
import { Box, Grid, Group, Space, Text } from '@zorro/web/design-system';
import { useDynamicRouter } from '@zorro/web/ui-utils';

import classNames from './CompanyQuickActionsOverview.module.scss';

type QuickActions = {
  icon: React.ReactNode;
  label: string;
  href: string;
};

type Props = {
  employerId: string;
  isDon: boolean;
  hasAllowanceModel?: boolean;
};

export const CompanyQuickActionsOverview = ({
  employerId,
  isDon,
  hasAllowanceModel = true,
}: Props) => {
  const { router } = useDynamicRouter({});

  const handleActionClick = async (href: string) => {
    const url = new URL(href, window.location.origin);
    const currentQueryParams = Object.fromEntries(
      new URL(window.location.href).searchParams
    );
    await router.push({
      pathname: url.pathname,
      query: { ...currentQueryParams, ...Object.fromEntries(url.searchParams) },
    });
  };

  const quickActionsState: QuickActions[] = [
    {
      icon: <AddUserIcon />,
      label: 'Add employee',
      href: isDon
        ? APP_ROUTES.DON_EMPLOYEES({ openWith: 'addEmployee' })
        : APP_ROUTES.OPS_EMPLOYERS_EMPLOYEES({
            employerId,
            openWith: 'addEmployee',
          }),
    },
    {
      icon: <InvoicesIcon />,
      label: 'View invoices',
      href: isDon
        ? APP_ROUTES.DON_DOCUMENTS('openInvoice')
        : APP_ROUTES.OPS_EMPLOYERS_DOCUMENTS({
            employerId,
            openWith: 'openInvoice',
          }),
    },
    {
      icon: <PayrollIcon />,
      label: 'Run payroll',
      href: isDon
        ? APP_ROUTES.DON_PAYROLL({ openWith: 'newPayroll' })
        : APP_ROUTES.OPS_EMPLOYERS_PAYROLL({
            employerId,
            openWith: 'newPayroll',
          }),
    },
  ];

  if (!hasAllowanceModel) {
    return (
      <Alert title="Allowance model pending" w="100%">
        To add employees, contact the Zorro team to upload an allowance model
      </Alert>
    );
  }

  return (
    <Box>
      <Text fw={600} size="md">
        Quick actions
      </Text>
      <Space h="md" />
      <Grid>
        <Grid.Col span={12}>
          <Grid>
            {quickActionsState.map((action) => (
              <Grid.Col span={6} key={action.label}>
                <Card
                  variant="white-border"
                  radius="md"
                  p="xs"
                  onClick={() => handleActionClick(action.href)}
                  className={classNames['root']}
                >
                  <Group align="center" p={0} m={0}>
                    <Box className={classNames['icon']}>{action.icon}</Box>
                    <Text>{action.label}</Text>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Grid.Col>
      </Grid>
    </Box>
  );
};
